import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Square, 
  Grid3X3, 
  Shapes, 
  Boxes, 
  Layers, 
  Building2,
  ArrowRight,
  HelpCircle
} from 'lucide-react';

const ServicesSection = () => {
  const services = [
    {
      icon: Square,
      title: 'Skimming',
      subtitle: 'Walls & Ceilings',
      description: 'Professional skim coating for a perfectly smooth, paint-ready finish. Ideal for new builds, renovations, and refreshing tired walls.',
      featured: true,
    },
    {
      icon: Grid3X3,
      title: 'Plasterboarding',
      subtitle: 'Dry Lining',
      description: 'Expert dry lining and plasterboard installation. Creating clean partitions, sound insulation, and smooth wall surfaces.',
    },
    {
      icon: Shapes,
      title: 'Patch Repairs',
      subtitle: 'Making Good',
      description: 'Seamless repairs for cracks, holes, and damaged plaster. We blend repairs to match your existing walls perfectly.',
    },
    {
      icon: Boxes,
      title: 'Cornice & Coving',
      subtitle: 'Decorative Mouldings',
      description: 'Elegant plastered cornice and coving to add character and a classic finishing touch where walls meet ceilings. Expertly fitted for a seamless, traditional look.',
    },
    {
      icon: Layers,
      title: 'Artex Covering',
      subtitle: 'Smooth Finish',
      description: 'Say goodbye to dated textured ceilings. We cover artex with a modern, smooth finish where appropriate.',
    },
    {
      icon: Building2,
      title: 'Rendering',
      subtitle: 'External Finishes',
      description: 'Traditional cement render and modern silicone render for exterior walls. Weather protection with lasting appeal.',
    },
  ];

  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contact');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      id="services" 
      className="py-20 lg:py-28 bg-white"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-red-600 font-semibold text-sm tracking-wider uppercase mb-4">
            What We Do
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 font-['Manrope'] mb-6">
            Our Plastering Services
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From small patch repairs to complete room renovations, we provide quality 
            plastering services for homes and properties across Northampton.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service, index) => (
            <Card 
              key={index}
              className={`group border border-slate-100 hover:border-red-200 shadow-sm hover:shadow-lg transition-all duration-300 service-card ${
                service.featured ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              data-testid={`service-card-${index}`}
            >
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-red-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-slate-900 font-['Manrope'] mb-1">
                    {service.title}
                  </h3>
                  <span className="text-sm text-red-600 font-medium">
                    {service.subtitle}
                  </span>
                </div>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Not Sure CTA */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-3 bg-slate-50 rounded-full px-6 py-4">
            <HelpCircle className="w-5 h-5 text-red-600" />
            <span className="text-slate-600">Not sure what you need?</span>
            <Button
              variant="link"
              className="text-red-600 hover:text-red-700 font-semibold p-0"
              onClick={scrollToContact}
              data-testid="not-sure-cta"
            >
              Send us photos and we'll advise
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
